import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

type Tier = 'launch' | 'grow' | 'dominate';

export const Demo: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [tier, setTier] = useState<Tier>('launch');
  const [activeLocation, setActiveLocation] = useState<string>('Roma');
  const [panel, setPanel] = useState<'home' | 'crm' | 'whatsapp' | 'reports' | 'settings' | 'calendar' | 'adv'>('crm');
  const location = useLocation();

  const tierTheme = (code: Tier) =>
    code === 'launch'
      ? { border: 'border-sky-200', pill: 'bg-sky-100 text-sky-700' }
      : code === 'grow'
      ? { border: 'border-emerald-200', pill: 'bg-emerald-100 text-emerald-700' }
      : { border: 'border-amber-200', pill: 'bg-amber-100 text-amber-700' };

  const theme = tierTheme(tier);

  const baseRows = (t('solutions.preview.common.rows', { returnObjects: true }) as any[]) || [];
  const [leads, setLeads] = useState<any[]>([]);

  // initialize leads from translations whenever language changes
  useEffect(() => {
    const withLocations = baseRows.map((r: any, idx: number) => ({
      ...r,
      location: idx === 0 ? 'Roma' : idx === 1 ? 'Bologna' : 'Milano',
    }));
    setLeads(withLocations);
  }, [i18n.language]);

  // read URL query for tier and panel
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get('tier') as Tier | null;
    const p = params.get('panel') as any;
    if (t && (['launch','grow','dominate'] as Tier[]).includes(t)) setTier(t);
    if (p && ['home','crm','whatsapp','reports','settings','calendar','adv'].includes(p)) setPanel(p);
  }, [location.search]);

  const handleNewLead = () => {
    const isIt = i18n.language.startsWith('it');
    const newLead = {
      name: isIt ? 'Nuovo Lead' : 'New Lead',
      phone: '+39 ' + Math.floor(300000000 + Math.random() * 699999999).toString().replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3'),
      channel: 'WhatsApp',
      status: { code: 'new', label: isIt ? 'Nuovo' : 'New' },
      last: isIt ? 'ora' : 'now',
      location: activeLocation,
    };
    setLeads((prev) => [newLead, ...prev]);
  };

  const handleExport = () => {
    const isDominate = tier === 'dominate';
    const headers = [
      t('solutions.preview.common.table.name') as string,
      t('solutions.preview.common.table.phone') as string,
      ...(isDominate ? [t('solutions.preview.common.table.location') as string] : []),
      t('solutions.preview.common.table.status') as string,
      t('solutions.preview.common.table.channel') as string,
      t('solutions.preview.common.table.last') as string,
    ];

    const rows = leads.map((r) => [
      r.name,
      r.phone,
      ...(isDominate ? [r.location || ''] : []),
      r.status?.label,
      r.channel,
      r.last,
    ]);

    const csv = [headers, ...rows]
      .map((line) => line.map((f) => '"' + String(f ?? '').replace(/"/g, '""') + '"').join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const ts = new Date().toISOString().slice(0,10);
    a.download = `leads_${tier}_${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleOpenCalendar = () => setPanel('calendar');
  const handleOpenADV = () => setPanel('adv');

  return (
    <div className="pt-24 pb-12 px-4 max-w-7xl mx-auto bg-white dark:bg-gray-900 min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-gray-100">{(t(`solutions.preview.header.${tier}`) as string)}</h1>
        <div className="flex gap-2">
          {(['launch','grow','dominate'] as Tier[]).map(code => (
            <button
              key={code}
              onClick={() => setTier(code)}
              className={`px-3 py-1.5 rounded-md border flex items-center gap-2 ${tier === code ? theme.border + ' bg-white dark:bg-gray-800 text-slate-900 dark:text-gray-100' : 'border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-300'} shadow-sm`}
              title={code}
            >
              <span>{code === 'launch' ? '🧩' : code === 'grow' ? '⚙️' : '📈'}</span>
              <span className="hidden sm:inline">{code.toUpperCase()}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Launch: Lite CRM, Grow: CRM + Analytics stronger, Dominate: adds BI & multi */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl border border-slate-200 dark:border-gray-700 bg-[#fafaff] dark:bg-gray-800 overflow-hidden shadow-sm">
        {/* Header */}
        <div className="h-12 px-4 flex items-center justify-between border-b border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="text-sm font-semibold text-slate-700 dark:text-gray-300">{(t(`solutions.preview.header.${tier}`) as string)}</div>
          <div className="text-xs text-slate-500 dark:text-gray-400">🔔 {(t('solutions.preview.common.notif') as string)}</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[56px_1fr_260px]">
          {/* Left navigation */}
          <div className="bg-white dark:bg-gray-800 border-r border-slate-200 dark:border-gray-700 py-3 hidden md:flex flex-col items-center gap-3">
            {([
              { key: 'home', icon: '🏠' },
              { key: 'crm', icon: '👥' },
              { key: 'whatsapp', icon: '💬' },
              { key: 'reports', icon: '📊' },
              { key: 'settings', icon: '⚙️' },
            ] as any[]).map((it) => (
              <button
                key={it.key}
                onClick={() => setPanel(it.key)}
                className={`w-8 h-8 rounded-md grid place-items-center ${panel===it.key ? 'bg-white dark:bg-gray-700 border border-slate-300 dark:border-gray-600 text-slate-900 dark:text-gray-100' : 'bg-slate-100 dark:bg-gray-700/50 text-slate-600 dark:text-gray-300'}`}
                title={String(it.key)}
              >
                {it.icon}
              </button>
            ))}
          </div>

          {/* Main workspace */}
          <div className="p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className={`px-2 py-1 text-[11px] rounded ${theme.pill}`}>{t('solutions.preview.common.filters.whatsapp') as string}</div>
              <div className={`px-2 py-1 text-[11px] rounded ${theme.pill}`}>{t('solutions.preview.common.filters.status') as string}: {t('solutions.preview.common.filters.statusInProgress') as string}</div>
              {tier !== 'launch' && (
                <>
                  <button onClick={handleOpenADV} className={`px-2 py-1 text-[11px] rounded ${theme.pill}`}>{t('solutions.preview.common.actions.adv') as string}</button>
                  <button onClick={handleOpenCalendar} className={`px-2 py-1 text-[11px] rounded ${theme.pill}`}>{t('solutions.preview.common.actions.calendar') as string}</button>
                </>
              )}
              {tier === 'dominate' && (
                <div className="ml-2 flex items-center gap-1">
                  <span className="text-[11px] text-slate-500 dark:text-gray-400">{t('solutions.preview.common.actions.location') as string}:</span>
                  {['Roma','Bologna','Milano'].map(loc => (
                    <button key={loc} onClick={() => setActiveLocation(loc)} className={`px-2 py-1 text-[11px] rounded border ${activeLocation===loc ? 'border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-700' : 'border-transparent bg-slate-100 dark:bg-gray-700/50 text-slate-600 dark:text-gray-300'}`}>{loc}</button>
                  ))}
                </div>
              )}
              <div className="ml-auto text-[10px] text-slate-400 dark:text-gray-500">{t('solutions.preview.common.powered') as string}</div>
            </div>

            {/* Action toolbar */}
            <div className="mb-2 flex items-center gap-2">
              <button onClick={handleNewLead} className="px-2 py-1.5 text-[11px] rounded-md border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-900 dark:text-gray-100 shadow-sm">➕ {t('solutions.preview.common.actions.newLead') as string}</button>
              <button onClick={handleExport} className="px-2 py-1.5 text-[11px] rounded-md border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-900 dark:text-gray-100 shadow-sm">⬇️ {t('solutions.preview.common.actions.export') as string}</button>
            </div>

            {/* Panels */}
            {panel === 'home' && (
              <div className="rounded-md border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
                <div className="text-sm font-semibold text-slate-700 dark:text-gray-300 mb-3">Dashboard</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="rounded border border-slate-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800">
                    <div className="text-[11px] text-slate-600 dark:text-gray-300">{t('solutions.preview.common.widgets.total') as string}</div>
                    <div className="text-[20px] font-bold text-slate-900 dark:text-gray-100">128</div>
                  </div>
                  <div className="rounded border border-slate-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800">
                    <div className="text-[11px] text-slate-600 dark:text-gray-300">{t('solutions.preview.common.widgets.avgResponse') as string}</div>
                    <div className="text-[16px] font-bold text-slate-900 dark:text-gray-100">2m 35s</div>
                  </div>
                  <div className="rounded border border-slate-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800">
                    <div className="text-[11px] text-slate-600 dark:text-gray-300">{t('solutions.preview.common.widgets.followup') as string}</div>
                    <div className="text-[16px] font-bold text-slate-900 dark:text-gray-100">86%</div>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  {tier!=='launch' && <button onClick={handleOpenADV} className="px-2 py-1 text-[11px] rounded border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-900 dark:text-gray-100 shadow-sm">📣 {t('solutions.preview.common.panels.adv') as string}</button>}
                  {tier!=='launch' && <button onClick={handleOpenCalendar} className="px-2 py-1 text-[11px] rounded border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-900 dark:text-gray-100 shadow-sm">📆 {t('solutions.preview.common.panels.calendar') as string}</button>}
                  <button onClick={() => setPanel('crm')} className="px-2 py-1 text-[11px] rounded border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-900 dark:text-gray-100 shadow-sm">👥 {t('solutions.preview.common.nav.leads') as string}</button>
                </div>
              </div>
            )}
            {panel === 'crm' && (
            <div className="rounded-md border border-slate-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800">
              {/* Intro card above table */}
              <div className="m-3 mb-0 rounded border-l-4 border-purple-400 dark:border-purple-500 bg-slate-50 dark:bg-gray-700/50 p-3">
                <p className="text-[13px] text-slate-700 dark:text-gray-300">
                  <strong>{t('solutions.preview.demoIntro.title') as string}</strong>
                </p>
                <p className="mt-1 text-[12px] text-slate-600 dark:text-gray-300">{(t(`solutions.preview.demoIntro.bodyByTier.${tier}`) as string) || (t('solutions.preview.demoIntro.body') as string)}</p>
              </div>
              <div className="grid grid-cols-5 text-[11px] font-semibold text-slate-600 dark:text-gray-300 bg-[#f5f5f8] dark:bg-gray-700/50 px-3 py-2 border-b border-slate-200 dark:border-gray-700">
                <div>{t('solutions.preview.common.table.name') as string}</div>
                <div>{t('solutions.preview.common.table.phone') as string}</div>
                {tier === 'dominate' && <div>{t('solutions.preview.common.table.location') as string}</div>}
                <div>{t('solutions.preview.common.table.status') as string}</div>
                <div>{t('solutions.preview.common.table.channel') as string}</div>
                <div>{t('solutions.preview.common.table.last') as string}</div>
              </div>
              {leads.map((r: any, idx: number) => (
                <div key={idx} className={`grid ${tier==='dominate'?'grid-cols-6':'grid-cols-5'} text-[11px] text-slate-700 dark:text-gray-300 px-3 py-2 ${idx < leads.length-1 ? 'border-b border-slate-100 dark:border-gray-700' : ''}`}>
                  <div>{r.name}</div>
                  <div>{r.phone}</div>
                  {tier === 'dominate' && <div>{r.location}</div>}
                  <div className={r.status?.code === 'in_progress' ? 'text-amber-700 dark:text-amber-500' : r.status?.code === 'closed' ? 'text-emerald-700 dark:text-emerald-500' : 'text-sky-700 dark:text-sky-400'}>{r.status?.label}</div>
                  <div>{r.channel}</div>
                  <div>{r.last}</div>
                </div>
              ))}
              {/* KPI mini-cards under table */}
              <div className="p-3 border-t border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="rounded border border-slate-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800">
                  <div className="text-[11px] text-slate-600 dark:text-gray-300">{t('solutions.preview.kpis.messagesWeek') as string}</div>
                  <div className="text-[20px] font-bold text-slate-900 dark:text-gray-100">+27</div>
                </div>
                <div className="rounded border border-slate-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800">
                  <div className="text-[11px] text-slate-600 dark:text-gray-300">{t('solutions.preview.kpis.autoReplies') as string}</div>
                  <div className="text-[20px] font-bold text-slate-900 dark:text-gray-100">+18</div>
                </div>
                <div className="rounded border border-slate-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800">
                  <div className="text-[11px] text-slate-600 dark:text-gray-300">{t('solutions.preview.kpis.contactsSaved') as string}</div>
                  <div className="text-[20px] font-bold text-slate-900 dark:text-gray-100">95%</div>
                </div>
              </div>
            </div>
            )}

            {panel === 'calendar' && (
              <div className="rounded-md border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
                <div className="text-sm font-semibold text-slate-700 dark:text-gray-300 mb-2">{t('solutions.preview.common.panels.calendar') as string}</div>
                <div className="grid grid-cols-7 gap-2 text-center text-[11px]">
                  {((t('solutions.preview.common.daysShort', { returnObjects: true }) as string[]) || []).map((d) => (
                    <div key={d} className="text-slate-500 dark:text-gray-400">{d}</div>
                  ))}
                  {Array.from({ length: 28 }).map((_, i) => (
                    <button key={i} className="h-10 rounded border border-slate-200 dark:border-gray-700 hover:border-sky-300 dark:hover:border-sky-500 hover:bg-sky-50 dark:hover:bg-sky-900/30 text-slate-900 dark:text-gray-100">{i + 1}</button>
                  ))}
                </div>
                <div className="mt-3 text-[11px] text-slate-500 dark:text-gray-400">{t('solutions.preview.common.actions.calendar') as string} • {t('solutions.preview.common.actions.location') as string}: {activeLocation}</div>
                <div className="mt-2">
                  <button onClick={() => setPanel('crm')} className="px-2 py-1 text-[11px] rounded border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-900 dark:text-gray-100 shadow-sm">← {t('solutions.preview.common.panels.crm') as string}</button>
                </div>
              </div>
            )}

            {panel === 'adv' && (
              <div className="rounded-md border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
                <div className="text-sm font-semibold text-slate-700 dark:text-gray-300 mb-2">{t('solutions.preview.common.panels.adv') as string}</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[{ name: 'Google Search' }, { name: 'Meta Leads' }, { name: 'Remarketing' }].map((c, i) => (
                    <div key={i} className="rounded border border-slate-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800">
                      <div className="text-[12px] font-semibold text-slate-800 dark:text-gray-200">{c.name}</div>
                      <div className="mt-2 text-[11px] text-slate-600 dark:text-gray-300">{t('solutions.preview.common.campaigns.budget') as string}: €{i === 0 ? '35' : i === 1 ? '25' : '15'}/d</div>
                      <div className="text-[11px] text-slate-600 dark:text-gray-300">{t('solutions.preview.common.campaigns.cpa') as string}: €{i === 0 ? '7.20' : i === 1 ? '5.80' : '9.50'}</div>
                      <div className="text-[11px] text-slate-600 dark:text-gray-300">{t('solutions.preview.common.campaigns.leads') as string}: {i === 0 ? '62' : i === 1 ? '44' : '22'}</div>
                      <div className="mt-2 text-[11px] text-slate-700 dark:text-gray-300">{t('solutions.preview.common.campaigns.status.active') as string}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-3">
                  <button onClick={() => setPanel('crm')} className="px-2 py-1 text-[11px] rounded border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-900 dark:text-gray-100 shadow-sm">← {t('solutions.preview.common.panels.crm') as string}</button>
                </div>
              </div>
            )}

            {panel === 'whatsapp' && (
              <div className="rounded-md border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
                <div className="text-sm font-semibold text-slate-700 dark:text-gray-300 mb-2">{t('solutions.preview.common.nav.whatsapp') as string}</div>
                <ul className="list-disc ml-5 text-[13px] text-slate-700 dark:text-gray-300">
                  {(((t('solutions.plans.items', { returnObjects: true }) as any[]) || []).find(p => p.code === tier)?.automations || (t(`solutions.diagram.${tier}.bullets`, { returnObjects: true }) as any[]) || []).map((a: string, i: number) => (
                    <li key={i}>{a}</li>
                  ))}
                </ul>
                <div className="mt-3">
                  <button onClick={() => setPanel('crm')} className="px-2 py-1 text-[11px] rounded border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-900 dark:text-gray-100 shadow-sm">← {t('solutions.preview.common.panels.crm') as string}</button>
                </div>
              </div>
            )}

            {panel === 'reports' && (
              <div className="rounded-md border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
                <div className="text-sm font-semibold text-slate-700 dark:text-gray-300 mb-3">{t('solutions.preview.common.nav.reports') as string}</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <div className="rounded border border-slate-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800">
                    <div className="text-[11px] text-slate-600 dark:text-gray-300">{t('solutions.preview.common.widgets.total') as string}</div>
                    <div className="text-[20px] font-bold text-slate-900 dark:text-gray-100">128</div>
                  </div>
                  <div className="rounded border border-slate-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800">
                    <div className="text-[11px] text-slate-600 dark:text-gray-300">{t('solutions.preview.common.widgets.avgResponse') as string}</div>
                    <div className="text-[16px] font-bold text-slate-900 dark:text-gray-100">2m 35s</div>
                  </div>
                  <div className="rounded border border-slate-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800">
                    <div className="text-[11px] text-slate-600 dark:text-gray-300">{t('solutions.preview.common.widgets.followup') as string}</div>
                    <div className="text-[16px] font-bold text-slate-900 dark:text-gray-100">86%</div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <div className="rounded border border-slate-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800">
                    <div className="text-[11px] text-slate-600 dark:text-gray-300 mb-1">{t('solutions.preview.common.widgets.trend') as string}</div>
                    <svg viewBox="0 0 100 40" className="w-full h-24">
                      <path d="M0 30 L10 26 L20 28 L30 24 L40 18 L50 20 L60 16 L70 22 L80 18 L90 26 L100 22" stroke={tier==='launch'?'#0ea5e9':tier==='grow'?'#10b981':'#f59e0b'} strokeWidth="2" fill="none" />
                      <path d="M0 40 L0 30 L10 26 L20 28 L30 24 L40 18 L50 20 L60 16 L70 22 L80 18 L90 26 L100 22 L100 40 Z" fill={tier==='launch'?'#0ea5e9':tier==='grow'?'#10b981':'#f59e0b'} opacity="0.1" />
                    </svg>
                  </div>
                  <div className="rounded border border-slate-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800">
                    <div className="text-[11px] text-slate-600 dark:text-gray-300 mb-1">{t('solutions.preview.common.widgets.channelMix') as string}</div>
                    <div className="grid grid-cols-3 gap-2 text-[11px]">
                      <div>
                        <div className="h-20 bg-sky-200 dark:bg-sky-900/30 rounded relative overflow-hidden"><div className="absolute bottom-0 left-0 right-0 h-[60%] bg-sky-500 dark:bg-sky-600" /></div>
                        <div className="mt-1 text-slate-600 dark:text-gray-300">WhatsApp</div>
                      </div>
                      <div>
                        <div className="h-20 bg-emerald-200 dark:bg-emerald-900/30 rounded relative overflow-hidden"><div className="absolute bottom-0 left-0 right-0 h-[45%] bg-emerald-500 dark:bg-emerald-600" /></div>
                        <div className="mt-1 text-slate-600 dark:text-gray-300">Web</div>
                      </div>
                      <div>
                        <div className="h-20 bg-violet-200 dark:bg-violet-900/30 rounded relative overflow-hidden"><div className="absolute bottom-0 left-0 right-0 h-[35%] bg-violet-500 dark:bg-violet-600" /></div>
                        <div className="mt-1 text-slate-600 dark:text-gray-300">Ads</div>
                      </div>
                    </div>
                  </div>
                  <div className="rounded border border-slate-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800">
                    <div className="text-[11px] text-slate-600 dark:text-gray-300 mb-1">{t('solutions.preview.common.widgets.funnel') as string}</div>
                    <div className="text-[11px] text-slate-700 dark:text-gray-300">
                      <div className="flex items-center gap-2 mb-1"><div className="w-20">{t('solutions.preview.common.widgets.leads') as string}</div><div className="flex-1 h-2 bg-slate-200 dark:bg-gray-700 rounded"><div className="h-2 bg-sky-500 dark:bg-sky-600 rounded w-[85%]" /></div><div className="text-slate-900 dark:text-gray-100">128</div></div>
                      <div className="flex items-center gap-2 mb-1"><div className="w-20">{t('solutions.preview.common.widgets.appointments') as string}</div><div className="flex-1 h-2 bg-slate-200 dark:bg-gray-700 rounded"><div className="h-2 bg-sky-500 dark:bg-sky-600 rounded w-[48%]" /></div><div className="text-slate-900 dark:text-gray-100">61</div></div>
                      <div className="flex items-center gap-2"><div className="w-20">{t('solutions.preview.common.widgets.conversion') as string}</div><div className="flex-1 h-2 bg-slate-200 dark:bg-gray-700 rounded"><div className="h-2 bg-sky-500 dark:bg-sky-600 rounded w-[22%]" /></div><div className="text-slate-900 dark:text-gray-100">22%</div></div>
                    </div>
                  </div>
                </div>
                {tier==='dominate' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <div className="rounded border border-slate-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800">
                      <div className="text-[11px] text-slate-600 dark:text-gray-300">{t('solutions.preview.common.widgets.revenue') as string}</div>
                      <div className="text-[16px] font-bold text-slate-900 dark:text-gray-100">€ 42.300</div>
                    </div>
                    <div className="rounded border border-slate-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800">
                      <div className="text-[11px] text-slate-600 dark:text-gray-300 mb-1">{t('solutions.preview.common.widgets.byLocation') as string}</div>
                      <div className="grid grid-cols-3 gap-2 text-[11px]">
                        {['Roma','Bologna','Milano'].map((loc, i) => (
                          <div key={loc} className="text-center">
                            <div className="h-16 bg-slate-200 dark:bg-gray-700 rounded relative overflow-hidden"><div className={`absolute bottom-0 left-0 right-0 ${i===0?'h-[70%] bg-sky-500 dark:bg-sky-600':i===1?'h-[50%] bg-emerald-500 dark:bg-emerald-600':'h-[40%] bg-amber-500 dark:bg-amber-600'}`} /></div>
                            <div className="mt-1 text-slate-600 dark:text-gray-300">{loc}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                <div className="mt-3">
                  <button onClick={() => setPanel('crm')} className="px-2 py-1 text-[11px] rounded border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-900 dark:text-gray-100 shadow-sm">← {t('solutions.preview.common.panels.crm') as string}</button>
                </div>
              </div>
            )}

            {panel === 'settings' && (
              <div className="rounded-md border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
                <div className="text-sm font-semibold text-slate-700 dark:text-gray-300 mb-2">{t('solutions.preview.common.nav.settings') as string}</div>
                <div className="flex items-center gap-3 text-[13px] text-slate-700 dark:text-gray-300">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" defaultChecked />
                    Notifications
                  </label>
                  <span className="text-slate-400 dark:text-gray-500">|</span>
                  <span>Lang: {i18n.language}</span>
                </div>
                <div className="mt-3">
                  <button onClick={() => setPanel('crm')} className="px-2 py-1 text-[11px] rounded border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-900 dark:text-gray-100 shadow-sm">← {t('solutions.preview.common.panels.crm') as string}</button>
                </div>
              </div>
            )}

            {tier !== 'launch' && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="rounded-md border border-slate-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800">
                  <div className="text-[11px] font-semibold text-slate-700 dark:text-gray-300">{t('solutions.preview.common.widgets.total') as string}</div>
                  <div className="text-[20px] font-bold text-slate-900 dark:text-gray-100">128</div>
                  {tier==='grow' && <div className="mt-2 text-[10px] text-slate-500 dark:text-gray-400">Google/Meta campaigns active: 2</div>}
                </div>
                <div className="rounded-md border border-slate-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800">
                  <div className="text-[11px] font-semibold text-slate-700 dark:text-gray-300">{t('solutions.preview.common.widgets.avgResponse') as string}</div>
                  <div className="text-[16px] font-bold text-slate-900 dark:text-gray-100">2m 35s</div>
                  {tier==='grow' && <div className="mt-2 text-[10px] text-slate-500 dark:text-gray-400">Auto reminders enabled</div>}
                </div>
              </div>
            )}
          </div>

          {/* Right analytics */}
          <div className="p-3 space-y-2 border-l border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <div className="rounded-md border border-slate-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800">
              <div className="text-[12px] font-semibold text-slate-800 dark:text-gray-200">{t('solutions.preview.followup.title') as string}</div>
              <div className="text-[16px] font-bold text-slate-900 dark:text-gray-100">86%</div>
              <div className="mt-2 rounded bg-[#f5f5f8] dark:bg-gray-700/50">
                <svg viewBox="0 0 100 40" className="w-full h-16">
                  <path d="M0 30 L15 26 L30 33 L45 20 L60 24 L75 18 L90 28 L100 22" stroke={tier==='launch'?'#0ea5e9':tier==='grow'?'#10b981':'#f59e0b'} strokeWidth="2" fill="none" />
                  <path d="M0 40 L0 30 L15 26 L30 33 L45 20 L60 24 L75 18 L90 28 L100 22 L100 40 Z" fill={tier==='launch'?'#0ea5e9':tier==='grow'?'#10b981':'#f59e0b'} opacity="0.12" />
                </svg>
              </div>
              <div className="mt-1 text-[10px] text-slate-500 dark:text-gray-400">{t('solutions.preview.followup.note') as string}</div>
            </div>
            {tier==='dominate' && (
              <>
                <div className="rounded-md border border-slate-200 dark:border-gray-700 p-2 bg-white dark:bg-gray-800">
                  <div className="text-[11px] font-semibold text-slate-700 dark:text-gray-300">{t('solutions.preview.common.widgets.revenue') as string}</div>
                  <div className="text-[16px] font-bold text-slate-900 dark:text-gray-100">€ 42.300</div>
                </div>
                <div className="rounded-md border border-slate-200 dark:border-gray-700 p-2 bg-white dark:bg-gray-800">
                  <div className="text-[11px] font-semibold text-slate-700 dark:text-gray-300">SLA</div>
                  <div className="text-[12px] text-slate-700 dark:text-gray-300">12h priority • multi-location</div>
                </div>
              </>
            )}
          </div>
        </div>
      </motion.div>

      {/* Final CTA */}
      <div className="mt-6 text-center">
        <div className="inline-block rounded-lg border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-5 py-4 shadow-sm">
          <div className="text-sm font-semibold text-slate-900 dark:text-gray-100">{t('solutions.preview.ctaFinal.title') as string}</div>
          <div className="mt-3">
            <Link to="/contact" className="inline-flex items-center px-4 py-2 rounded-md bg-violet-600 dark:bg-violet-700 text-white text-sm font-semibold shadow hover:shadow-md hover:bg-violet-700 dark:hover:bg-violet-600">{t('solutions.preview.ctaFinal.button') as string}</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Demo;


